/**
 * Inline-fallback accounting for the pooled expensive drivers.
 *
 * Every pooled expensive wrapper (`runFfmpeg`, `formatSnapshotMaybeOffloaded`,
 * `augmentTailMaybeOffloaded`) degrades to running the work **inline on the host**
 * when its pool faults. That is the right call for a one-off wedge — the work
 * itself never breached a limit, so the host can safely run it. But the fallback
 * runs *outside* the shared expensive gate, so if the fault is **systemic** (a
 * missing/broken runner bundle, a crash-looping runner) every routed call site
 * degrades at once and the convergence bound silently stops applying — precisely
 * when it matters most.
 *
 * This module is the counter that makes that visible. It lives at **module scope**,
 * deliberately not on any pool instance: the worst systemic fault is the runner
 * entry failing to resolve, which throws inside the pool constructor and leaves the
 * driver's pool singleton `null` — so `get*Stats()` returns `null` and the host's
 * per-driver telemetry skips the driver entirely. A counter held by the pool would
 * be blind in exactly the case it exists to catch.
 *
 * Counts are cumulative since process start (matching every other
 * `ExpensivePool*` counter); the host takes per-interval deltas so a `Sum` over a
 * period reads as a rate.
 *
 * @module worker-pool/inline-fallback
 */

/**
 * Why a caller ran the work inline instead of on a pooled child. These are read
 * differently in ops, so they stay separate dimensions rather than one counter:
 *
 * - `pool_unavailable` — the pool could not be obtained at all (runner entry
 *   unresolvable, construction threw). **Systemic**: every call at this driver is
 *   degraded, not just this one. Never expected; alarm on a single occurrence.
 * - `runner_fault` — a built pool's child crashed, wedged (backstop timeout), or
 *   could not be spawned for this call. **Per-call**; an isolated one is normal, so
 *   alarm on a sustained rate.
 * - `limit_breach` — the child hit its own wall-clock/RSS limit and the caller ran
 *   inline anyway. Containment *worked*, so this is not a pool fault and must not
 *   inflate the alarmed series. Only bounded work opts into this (`augment-tail`);
 *   ffmpeg returns the breach to its caller and `format-snapshot` returns its
 *   timeout marker rather than re-running a pathological input on the host.
 *
 * The fallback taken because a pool is **disabled** is not recorded at all — that
 * is the configured path, not a fault.
 */
export type InlineFallbackReason = 'pool_unavailable' | 'runner_fault' | 'limit_breach';

/** One driver/reason pair's cumulative count since process start. */
export interface InlineFallbackCount {
  /** Driver label, matching `WorkerDriver.name` (`ffmpeg`, `cpu-offload`). */
  driver: string;
  reason: InlineFallbackReason;
  count: number;
}

const REASONS: readonly InlineFallbackReason[] = [
  'pool_unavailable',
  'runner_fault',
  'limit_breach',
];

/** `${driver}:${reason}` → cumulative count. */
const counts = new Map<string, number>();

/** Record one inline fallback. Cheap enough to call on any fault path. */
export function recordInlineFallback(driver: string, reason: InlineFallbackReason): void {
  const key = `${driver}:${reason}`;
  counts.set(key, (counts.get(key) ?? 0) + 1);
}

/**
 * Cumulative counts, one entry per driver/reason pair that has fired at least
 * once. Pairs that never fired are omitted rather than reported as 0, so the host
 * emits no datapoint for them — an absent series reads as "never fell back".
 */
export function inlineFallbackCounts(): InlineFallbackCount[] {
  const out: InlineFallbackCount[] = [];
  for (const [key, count] of counts) {
    const reason = REASONS.find((r) => key.endsWith(`:${r}`));
    if (!reason) continue;
    out.push({ driver: key.slice(0, key.length - reason.length - 1), reason, count });
  }
  return out;
}

/** @internal test seam. */
export function _resetInlineFallbacksForTest(): void {
  counts.clear();
}
